import { formatCodeableConcept, type WithId } from '@medplum/core';
import type { Condition } from '@medplum/fhirtypes';
import { format, parseISO } from 'date-fns';
import { ArrowLeftIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { DATE_FORMATS } from '../../utils/date';
import { Button } from '../ui/button';
import { Item, ItemContent, ItemHeader, ItemTitle } from '../ui/item';

export type ConditionDetailProps = {
  condition: WithId<Condition>;
  onBack?: () => void;
};

export const ConditionDetail = ({ condition, onBack }: ConditionDetailProps): React.ReactElement => {
  const { t } = useTranslation('records');
  const icd10Code = condition.code?.coding?.find((c) => c.system?.includes('icd-10'))?.code;

  const items = [
    {
      title: t('verification'),
      value: condition.verificationStatus ? formatCodeableConcept(condition.verificationStatus) : t('unknown'),
    },
    {
      title: t('category'),
      value: condition.category?.at(0) ? formatCodeableConcept(condition.category.at(0)!) : t('unknown'),
    },
    {
      title: t('onsetDate'),
      value: condition.onsetDateTime
        ? format(parseISO(condition.onsetDateTime), DATE_FORMATS.DATE)
        : condition.onsetPeriod?.start
          ? format(parseISO(condition.onsetPeriod.start), DATE_FORMATS.DATE)
          : t('unknown'),
    },
    {
      title: t('recordedDate'),
      value: condition.recordedDate ? format(parseISO(condition.recordedDate), DATE_FORMATS.DATE) : t('unknown'),
    },
  ];

  return (
    <div className="mx-auto w-full max-w-4xl p-5">
      {onBack && (
        <Button
          id="back-to-conditions-button"
          variant="ghost"
          onClick={onBack}
          className="mb-4 flex items-center gap-2"
        >
          <ArrowLeftIcon className="size-4" />
          <span>{t('backToConditions')}</span>
        </Button>
      )}

      <Item>
        <ItemHeader className="flex flex-col items-start gap-2">
          {icd10Code && (
            <span className="flex items-center font-mono text-xs text-muted-foreground">
              {t('icd10')} {icd10Code}
            </span>
          )}

          <ItemTitle className="text-lg font-medium">
            {condition.code ? formatCodeableConcept(condition.code) : t('unknownCondition')}
          </ItemTitle>
        </ItemHeader>
        <ItemContent>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {items.map((item) => (
              <div key={item.title}>
                <div className="mb-1 text-muted-foreground">{item.title}</div>
                <div>{item.value}</div>
              </div>
            ))}
          </div>
        </ItemContent>
      </Item>
    </div>
  );
};
